import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/react-hoc';
import get from 'lodash/get';
import invoke from 'lodash/invoke';
import { FormattedDate, defineMessages, injectIntl } from 'react-intl';
import ScheduleModal from '../../components/ScheduleModal';
import Toast, { ToastKind } from '../../components/Toast';
import rsvpCollegeFairMutation from '../../mutations/HighSchool/rsvpCollegeFair';
import { timeTrim } from '../VisitRequest/time-helpers';
import { SimpleGraphQLComponent } from '../../utils/apollo';

const messages = defineMessages({
  cancel: {
    id: 'common.button.cancel',
    defaultMessage: 'CANCEL',
  },
  request: {
    id: 'schedule.fairs.confirm',
    defaultMessage: 'Yes, Submit Request',
  },
  prompt: {
    id: 'schedule.fairs.propmt',
    defaultMessage: 'Do you want to request to attend this fair with {name} from ',
  },
  title: {
    id: 'schedule.fairs.title',
    defaultMessage: 'Request to attend?',
  },
  error: {
    id: 'schedule.fairs.notifications.error',
    defaultMessage: 'Sorry, this fair is no longer available. Please select another fair.',
  },
  approvedDescription: {
    id: 'schedule.fairs.notifications.approvedDescription',
    defaultMessage: 'Your request has been automatically confirmed by the high school.',
  },
  approvedTitle: {
    id: 'schedule.fairs.notifications.approvedTitle',
    defaultMessage: 'Fair registration confirmed!',
  },
  pendingDescription: {
    id: 'schedule.fairs.notifications.pendingDescription',
    defaultMessage: 'You will receive an email notification when your request has been confirmed.',
  },
  pendingTitle: {
    id: 'schedule.fairs.notifications.pendingTitle',
    defaultMessage: 'Fair registration requested!',
  },
});

class FairRequest extends React.PureComponent {
  static propTypes = {
    highSchoolName: PropTypes.string,
    fairNode: PropTypes.shape({
      timezone: PropTypes.string,
      id: PropTypes.string,
      date: PropTypes.string,
      startTime: PropTypes.string,
      endTime: PropTypes.string,
    }),
    onClose: PropTypes.func,
    mountNode: PropTypes.any,
    onOpen: PropTypes.func,
    open: PropTypes.bool,
    onConfirm: PropTypes.func,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    rsvpCollegeFair: PropTypes.func.isRequired,
  };

  state = { notifications: [] };

  // eslint-disable-next-line react/no-access-state-in-setstate
  removeNotification = () => { this.state.notifications.shift(); this.setState({ notifications: this.state.notifications }); }

  newNotification = (title, message, kind) => {
    // eslint-disable-next-line react/no-access-state-in-setstate
    const localNotifications = this.state.notifications.slice();
    const notification = <Toast active title={title} description={message} onDeactivate={this.removeNotification} kind={kind} />;
    localNotifications.push(notification);
    this.setState({ notifications: localNotifications });
  };

  reserve = () => {
    const { intl, rsvpCollegeFair, fairNode } = this.props;
    invoke(this.props, 'onConfirm');
    const onFailure = () => {
      this.newNotification('', intl.formatMessage(messages.error), ToastKind.error);
      invoke(this.props, 'onClose');
    };

    const onSuccess = (response) => {
      const status = get(response, 'data.rsvpCollegeFair.collegeFair.attending');
      if (status === 'APPROVED') {
        this.newNotification(intl.formatMessage(messages.approvedTitle),
          intl.formatMessage(messages.approvedDescription),
          ToastKind.success);
      } else if (status === 'PENDING') {
        this.newNotification(intl.formatMessage(messages.pendingTitle),
          intl.formatMessage(messages.pendingDescription),
          ToastKind.success);
      }
      invoke(this.props, 'onClose');
    };

    rsvpCollegeFair({
      variables: {
        input: {
          collegeFair: {
            id: fairNode.id,
          },
        },
      },
    }).then(onSuccess, onFailure);
  };

  render() {
    const { onClose, highSchoolName, fairNode, mountNode, onOpen, open, intl } = this.props;
    const timezone = get(fairNode, 'timezone', '');
    const startTime = get(fairNode, 'startTime');
    const endTime = get(fairNode, 'endTime');
    const timeStr = timeTrim(startTime, endTime);

    const props = { onClose, mountNode, onOpen, open };

    return (
      <div>
        {this.state.notifications}
        <ScheduleModal
          title={intl.formatMessage(messages.title)}
          message={(
            <div>
              {intl.formatMessage(messages.prompt, { name: highSchoolName })}
              <b>{`${timeStr} ${timezone}`}</b> on
              <b>
                <FormattedDate value={`${fairNode.date}T12:00:00`} weekday="long" year="numeric" month="long" day="numeric" />
              </b>
            </div>
          )}
          cancelText={intl.formatMessage(messages.cancel)}
          confirmText={intl.formatMessage(messages.request)}
          onCancel={onClose}
          onConfirm={this.reserve}
          {...props}
        />
      </div>
    );
  }
}

const mutateRSVPCollegeFair = graphql(rsvpCollegeFairMutation, {
  name: 'rsvpCollegeFair',
  options: {
    refetchQueries: ['RepVisitsOverview', 'AvailabilityCalendar', 'RepVisitsYourSchedule'],
  },
});

export const FairRequestComponent = injectIntl(FairRequest);
export default SimpleGraphQLComponent(mutateRSVPCollegeFair)(FairRequestComponent);
